import React, { useCallback, useId, useMemo } from 'react';
import './ProductGridColumnsToggle.scss';

export type ProductGridColumnsValue = 1 | 2 | 3 | 4;
type ToggleVariant = 'desktop' | 'tablet' | 'mobile';
interface ProductGridColumnsToggleProps {
    value: ProductGridColumnsValue;
    onChange: (value: ProductGridColumnsValue) => void;
    options?: readonly ProductGridColumnsValue[];
    variant?: ToggleVariant;
    className?: string;
    disabled?: boolean;
    ariaLabel?: string;
}

function cx(...parts: Array<string | undefined | false>): string {
    return parts.filter(Boolean).join(' ');
}
function formatColumnsLabel(n: ProductGridColumnsValue): string {
    if (n === 1) return '1 колонка';
    return `${n} колонки`;
}

const DEFAULT_OPTIONS: readonly ProductGridColumnsValue[] = [3, 4];
const getIconCols = (variant: ToggleVariant, opt: ProductGridColumnsValue): ProductGridColumnsValue => {
    if (variant === 'desktop') {
        if (opt === 3) return 2;
        if (opt === 4) return 3;
    }
    return opt;
};

const ProductGridColumnsToggle: React.FC<ProductGridColumnsToggleProps> = ({
    value,
    onChange,
    options = DEFAULT_OPTIONS,
    variant = 'desktop',
    className,
    disabled = false,
    ariaLabel = 'Вид сетки товаров',
}) => {
    const groupId = useId();

    const safeOptions = useMemo(() => {
        return options.length > 0 ? options : DEFAULT_OPTIONS;
    }, [options]);

    const selectedIndex = useMemo(() => {
        const idx = safeOptions.findIndex((o) => o === value);
        return idx >= 0 ? idx : 0;
    }, [safeOptions, value]);

    const setByIndex = useCallback(
        (idx: number) => {
            const next = safeOptions[Math.max(0, Math.min(safeOptions.length - 1, idx))];
            if (next !== undefined && next !== value) onChange(next);
        },
        [onChange, safeOptions, value],
    );

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLDivElement>) => {
            if (disabled) return;
            if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
                e.preventDefault();
                setByIndex(selectedIndex + 1);
            }
            if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
                e.preventDefault();
                setByIndex(selectedIndex - 1);
            }
            if (e.key === 'Home') {
                e.preventDefault();
                setByIndex(0);
            }
            if (e.key === 'End') {
                e.preventDefault();
                setByIndex(safeOptions.length - 1);
            }
        },
        [disabled, selectedIndex, setByIndex, safeOptions.length],
    );

    return (
        <div
            className={cx('product-grid-toggle', disabled && 'product-grid-toggle--disabled', className)}
            role="radiogroup"
            aria-label={ariaLabel}
            aria-disabled={disabled || undefined}
            onKeyDown={handleKeyDown}
            data-variant={variant}
        >
            {safeOptions.map((opt) => {
                const isActive = opt === value;
                // const rows = opt === 1 ? 1 : 2;
                // const cellsCount = opt === 1 ? 1 : opt * 2;
                const iconCols = getIconCols(variant, opt);
                const rows = iconCols === 1 ? 1 : 2;
                const cellsCount = iconCols === 1 ? 1 : iconCols * 2;

                return (
                    <button
                        key={opt}
                        type="button"
                        className={cx('product-grid-toggle__option', isActive && 'product-grid-toggle__option--active')}
                        role="radio"
                        aria-checked={isActive}
                        aria-labelledby={`${groupId}-${opt}-label`}
                        disabled={disabled}
                        tabIndex={isActive ? 0 : -1}
                        onClick={() => onChange(opt)}
                    >
                        <span
                            className={`product-grid-toggle__icon ${isActive && 'product-grid-toggle__icon--active'}`}
                            aria-hidden="true"
                            style={
                                {
                                    // ['--cols' as string]: String(opt),
                                    ['--cols' as string]: String(iconCols),
                                    ['--rows' as string]: String(rows),
                                } as React.CSSProperties
                            }
                        >
                            {Array.from({ length: cellsCount }).map((_, i) => (
                                <span key={i} className={`product-grid-toggle__cell ${isActive && 'product-grid-toggle__cell--active'}`} />
                            ))}
                        </span>

                        <span id={`${groupId}-${opt}-label`} className="product-grid-toggle__sr-only">
                            {formatColumnsLabel(opt)}
                        </span>
                    </button>
                );
            })}
        </div>
    );
};

export default ProductGridColumnsToggle;
